import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FormProvider, useFieldArray, useForm} from "react-hook-form";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClose, faEnvelope, faLink, faPhone} from "@fortawesome/free-solid-svg-icons";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs from "dayjs";

// Utils
import {AuthContext} from "../../contexts/auth.js";
import useApi from "../../hooks/useApi.js";
import useMountedState from "../../hooks/useMountedState.js";
import {prettyDateInUserTimezone} from "../../utils/helpers.js";
import {megabytesToBytes, uploadSingleFile, uploadErrorMessage} from "../../utils/fileUpload.js";

// Contexts
import {SettingsContext} from "../../contexts/settings.js";
import {useToast} from "../../contexts/toast.js";

// Components
import Modal from "../../components/Modal.js";
import SearchSelect from "../../components/SearchSelect.js";
import {
  InputTextArea,
  InputText,
  InputDate,
  InputError,
  InputFileGroup,
  InputCheck
} from "../../components/form/FormInputs.js";

// Style
import {pad, radius} from "../../style/components/variables.js";
import {voice} from "../../style/components/typography.js";
import {flex} from "../../style/components/mixins.js";
import {
  Form,
  FormGroup,
  FormField,
  ButtonFull,
  HeadingCenter,
  ButtonLoader,
  Inline,
  Abbr,
  Button
} from "../../style/components/general.js";

const defaultValues = {
  id: "",
  name: "",
  date: dayjs().format("YYYY-MM-DD"),
  message: ""
};

const schema = yup.object().shape({
  name: yup.string().required("Please provide label"),
  message: yup.string().required("Please provide text"),
  date: yup.date().required("Please provide a date"),
  tags: yup
    .array()
    .of(yup.object())
    .test({
      test: val => Array.isArray(val) && val.length > 0,
      message: "Please provide at least one tag."
    })
});

const ModalRowAdd = ({
  visible,
  setVisible,
  facilityId,
  selected,
  setSelected,
  updateTable,
  tags: defaultTags,
  tableId
}) => {
  const isMounted = useMountedState();

  const {currentUser, roles} = useContext(AuthContext);
  const {settings} = useContext(SettingsContext);
  const {addToast} = useToast();

  const maxBytes = useMemo(() => {
    let {maxFileSize: current} = settings;
    if (!current) current = 50;
    else {
      current = parseInt(current, 10);
      if (!current || Number.isNaN(current)) current = 50;
      else if (current > 100) current = 100;
    }
    return megabytesToBytes(current);
  }, [settings]);

  const [allTags, setAllTags] = useState([]);
  const [tags, setTags] = useState(null);
  const [files, setFiles] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [restrictTo, setRestrictTo] = useState([]);
  const [error, setError] = useState(null);

  const {api, loading} = useApi("custom-table-rows");
  const {api: apiTags} = useApi("tags");

  const available = roles?.filter(
    curr => curr?.permissions?.resource?.byId?.custom_table_row?.view === "enabled"
  );

  const form = useForm({
    defaultValues: selected
      ? {
          ...defaultValues,
          ...selected,
          restrictRole: !!selected.roles?.length,
          files: selected.files.map(file => ({id: file.id, filename: file.label})),
          date: selected.date
            ? prettyDateInUserTimezone(selected.date, settings?.timezone, "YYYY-MM-DD")
            : undefined
        }
      : {
          ...defaultValues,
          tags: defaultTags
        },
    resolver: yupResolver(schema)
  });
  const {
    setValue,
    control,
    watch,
    clearErrors,
    formState: {errors},
    handleSubmit
  } = form;

  const {append: appendTag, remove: removeTag} = useFieldArray({control, name: "tags"});

  const selectedTags = watch("tags");
  const restrictRole = watch("restrictRole");

  // Load tags
  useEffect(() => {
    if (isMounted()) {
      apiTags.callGet().then(response => {
        if (response.status === 200)
          setAllTags(response.data.map(tag => ({name: tag.label, label: tag.label})));
      });

      if (selected) setRestrictTo(selected.roles);
    }
  }, [isMounted, apiTags, selected]);

  const searchTags = query => {
    if (query) {
      const lower = query.toLowerCase();
      const tempResults = allTags.filter(option => option.label.toLowerCase().includes(lower));
      tempResults.sort(
        (a, b) => a.label.toLowerCase().indexOf(lower) - b.label.toLowerCase().indexOf(lower)
      );
      setTags(tempResults);
    } else setTags(allTags);
  };

  // Reset tag error
  useEffect(() => {
    if (selectedTags && selectedTags.length > 0) clearErrors("tags");
  }, [clearErrors, selectedTags]);

  const handleSave = useCallback(
    async values => {
      const {name, date, message, tags: updatedTags, files: updatedFiles} = values;

      if (restrictRole && !restrictTo?.length) {
        setError("Please select roles.");
        return;
      }

      const params = {
        userId: currentUser.publicId,
        facilityId,
        tableId,
        name,
        date,
        message,
        tags: updatedTags.map(tag => tag.label),
        roles: restrictTo?.map(({id}) => id)
      };

      let newFileIds = [];
      if (files.length > 0) {
        try {
          const results = await Promise.all(
            files.map(file => uploadSingleFile(file, {userId: currentUser.publicId, maxBytes}))
          );
          newFileIds = results.map(({id}) => id).filter(Boolean);
        } catch (e) {
          addToast(uploadErrorMessage(e), "error");
          return;
        }
      }

      if (newFileIds.length > 0) params.fileIds = newFileIds.join(",");

      if (selected) {
        const old = selectedTags.map(tag => tag.name);
        const diff = updatedTags.filter(tag => old.includes(tag.name)).length === 0;

        const deletedFiles = selected.files.filter(
          oldFile => !updatedFiles.some(newFile => oldFile.label === newFile.filename)
        );
        params.delete = deletedFiles.map(file => file.id);

        api.callPut(selected.id, {}, {params}).then(() => {
          if (diff) window.location.reload();
          else {
            setSelected([]);
            updateTable();
            setVisible(false);
          }
        });
      } else {
        api.callPost({}, {params}).then(() => {
          setVisible(false);
          updateTable();
        });
      }
    },
    [
      addToast,
      api,
      currentUser,
      facilityId,
      files,
      maxBytes,
      restrictRole,
      restrictTo,
      selected,
      selectedTags,
      setSelected,
      tableId,
      updateTable,
      setVisible
    ]
  );

  // Reset on Component unmount
  useEffect(
    () => () => {
      if (!isMounted()) setSelected(null);
    },
    [isMounted, setSelected]
  );

  const search = query => {
    if (query) {
      const lower = query.toLowerCase();
      const tempResults = available.filter(
        option =>
          option.label.toLowerCase().includes(lower) &&
          !restrictTo?.map(role => role.id).includes(option.id)
      );
      tempResults.sort(
        (a, b) => a.label.toLowerCase().indexOf(lower) - b.label.toLowerCase().indexOf(lower)
      );
      setSearchResults(tempResults);
    } else
      setSearchResults(available.filter(role => !restrictTo?.map(r => r.id).includes(role.id)));
  };

  return (
    <Modal visible={visible} setVisible={setVisible}>
      <ModalTitle>{selected ? "Edit Row" : "Add Row"}</ModalTitle>

      <FormProvider {...form}>
        <Form onSubmit={handleSubmit(handleSave)} noValidate>
          <FormGroup>
            {selected && (
              <FormField>
                <SearchSelect
                  label="Tags"
                  help="A tag controls which tables a row appears in. Rows show up in every table that includes one of their tags, so changing a tag moves the row to tables with that tag."
                  results={tags}
                  setResults={setTags}
                  search={searchTags}
                  add={result => {
                    if (result.name) appendTag(result);
                    else appendTag({name: result, label: result});
                  }}
                  allowNew
                />
                {selectedTags && selectedTags.length > 0 && (
                  <Selections>
                    {selectedTags.map(tag => (
                      <Selection key={tag.name} onClick={() => removeTag(tag.name)}>
                        {tag.label}&nbsp;
                        <FontAwesomeIcon icon={faClose} />
                      </Selection>
                    ))}
                  </Selections>
                )}
                <InputError errors={errors} name="tags" />
              </FormField>
            )}
            <FormField>
              <InputText name="name" placeholder="Label" />
            </FormField>
            <FormField>
              <InputDate name="date" />
            </FormField>
            <FormField>
              <Inline>
                <IconButton
                  type="button"
                  onClick={() =>
                    setValue("message", `${watch("message")}tel=1111111111;name=Phone Name;`)
                  }>
                  <Abbr title="Add Phone">
                    <FontAwesomeIcon icon={faPhone} />
                  </Abbr>
                </IconButton>
                <IconButton
                  type="button"
                  onClick={() =>
                    setValue(
                      "message",
                      `${watch("message")}mail=example@hero-services.com;name=Email Name;`
                    )
                  }>
                  <Abbr title="Add Mail">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </Abbr>
                </IconButton>
                <IconButton
                  type="button"
                  onClick={() =>
                    setValue(
                      "message",
                      `${watch("message")}url=https://hero-services.com;name=Link Name;`
                    )
                  }>
                  <Abbr title="Add Link">
                    <FontAwesomeIcon icon={faLink} />
                  </Abbr>
                </IconButton>
              </Inline>
              <InputTextArea name="message" placeholder="Message" />
            </FormField>
            <FormField>
              <InputFileGroup name="files" setFiles={setFiles} />
            </FormField>
            {roles?.length > 0 && (
              <FormField>
                <InputCheck name="restrictRole">Restrict access by role?</InputCheck>
                {restrictRole && (
                  <Wrapper>
                    <SearchSelect
                      placeholder="Find role(s)..."
                      results={searchResults}
                      setResults={setSearchResults}
                      search={search}
                      add={({id, name, label}) => {
                        setRestrictTo(prev => {
                          if (prev && !prev.map(curr => curr.id).includes(id))
                            return [...prev, {id, name, label}];
                          return prev ?? [{id, name, label}];
                        });
                      }}
                      showAll
                    />
                    <StyledInline>
                      {restrictTo?.map(({name, label, id}) => (
                        <SelectedRole key={name}>
                          <Abbr title={label.toUpperCase()}>{label.toUpperCase()}</Abbr>
                          <IconButton
                            onClick={() => {
                              setRestrictTo(prev => {
                                let toRemove;
                                prev.map((role, index) => {
                                  if (role.id === id) toRemove = index;
                                });
                                const newRoles = [...prev];
                                newRoles.splice(toRemove, 1);
                                return newRoles;
                              });
                            }}>
                            <FontAwesomeIcon icon={faClose} />
                          </IconButton>
                        </SelectedRole>
                      ))}
                      <InputError errors={{restrictTo: error}} name="restrictTo" />
                    </StyledInline>
                  </Wrapper>
                )}
              </FormField>
            )}
          </FormGroup>
          <ButtonFull type="submit" loading={loading ? 1 : 0}>
            Save{loading && <ButtonLoader />}
          </ButtonFull>
        </Form>
      </FormProvider>
    </Modal>
  );
};

ModalRowAdd.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  updateTable: PropTypes.func.isRequired,
  selected: PropTypes.objectOf(PropTypes.any),
  setSelected: PropTypes.func,
  facilityId: PropTypes.number,
  tags: PropTypes.arrayOf(PropTypes.any),
  tableId: PropTypes.number
};

ModalRowAdd.defaultProps = {
  selected: null,
  setSelected: null,
  facilityId: null,
  tags: [],
  tableId: null
};

// Style Overrides
const ModalTitle = styled(HeadingCenter)`
  margin: ${pad}px 0;
`;

const Selections = styled.div`
  position: relative;
  ${flex("row", "wrap", "start", "start")};
`;

const Selection = styled.button`
  display: flex;
  color: ${({theme}) => theme.tertiary};
  background: ${({theme}) => theme.primary};
  border-radius: ${radius};
  padding: ${pad / 2}px;
  width: fit-content;
  margin: ${pad}px ${pad / 2}px 0 0;
  ${voice.quiet};
`;

const Wrapper = styled.div`
  margin-top: ${pad}px;
  width: 100%;
`;

const SelectedRole = styled.div`
  ${flex("row", "nowrap", "space-between", "center")};
  flex: 1;
  width: max-content;
  border-radius: ${radius};
  color: ${({theme}) => theme.tertiary};
  background-color: ${({theme}) => theme.secondary};
  padding: ${pad / 2}px ${pad}px;

  ${Abbr} {
    ${voice.quiet};
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: min-content;
  }
`;

const IconButton = styled(Button)`
  ${voice.small};
  background-color: transparent;
  width: min-content;
  padding: 0;
  margin-left: ${pad / 2}px;

  svg {
    fill: ${({theme}) => theme.tertiary};
  }
`;

const StyledInline = styled(Inline)`
  margin-top: ${pad}px;
`;

export default ModalRowAdd;
